<script lang="ts" setup>
import OmniDialog from "@/components/omni/OmniDialog.vue";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import type { RouteStopDto } from "@/api/trips/dto/trip.dto";
import { OrderStopType } from "@/constants/order";
import { computed, ref, useTemplateRef, watch } from "vue";
import type { VForm } from "vuetify/components";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import { useDateTime } from "@/composables";
import validationMixin from "@/mixins/validation.mixin";
import dayjs from "dayjs";
import { useError } from "@/composables/useError";
import TimezoneSelect from "@/components/selects/TimezoneSelect.vue";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import type { TripMonitorTruck } from "@/api/trips/dto/trip-monitor";
import TruckLink from "@/components/links/TruckLink.vue";

const showDialog = defineModel<boolean>({ default: false });

const api = new API();

const { showSnackbarError } = useSnackbarStore();
const { timezonesShortList, dateToUTC } = useDateTime();
const { requiredValidator } = validationMixin.methods!;
const { showAPIError } = useError();

const formRef = useTemplateRef<VForm | null>("form");
const loading = ref<boolean>(false);
const goodToGoData = ref<{
  date: string;
  time: string;
  timezone: string;
}>({ date: "", time: "", timezone: "" });

const props = defineProps<{
  stop: RouteStopDto;
  tripId: number;
  truck: TruckDto | TripMonitorTruck;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "finish"): void;
}>();

const stopType = computed(() => {
  switch (props.stop.type) {
    case OrderStopType.pickup:
      return "Pick up";
    case OrderStopType.delivery:
      return "Delivery";
    default:
      return "";
  }
});

async function checkIn() {
  if (!formRef.value) {
    return;
  }

  const { valid } = await formRef.value.validate();
  if (!valid) return;

  loading.value = true;

  try {
    const response = await api.trips.checkOutRouteStop(props.tripId, props.stop.id!, {
      checkOutTime: dateToUTC(goodToGoData.value.date + " " + goodToGoData.value.time, goodToGoData.value.timezone),
    });

    if (response.success) {
      emits("finish");
      emits("update:modelValue", false);
    } else {
      showAPIError(response, "Error checking out at stop");
    }
  } catch (error) {
    console.error("Check-out error:", error);
    showSnackbarError("Unexpected error during check-out");
  } finally {
    loading.value = false;
  }
}

watch(
  showDialog,
  async (value) => {
    if (value) {
      goodToGoData.value.timezone = props.stop.facility?.timezone || "";
      const date = dayjs().tz(goodToGoData.value.timezone);
      goodToGoData.value.date = date.format("YYYY-MM-DD");
      goodToGoData.value.time = date.format("HH:mm");
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :disabled="loading"
    :loading="loading"
    :title="'Good to go on the ' + stopType"
    primary-action-label="Good to go"
    @primary-action:click="checkIn"
  >
    <v-form ref="form">
      <div class="mb-4 text-grey-darken-1">
        You are about to check out
        <truck-link :truck-id="truck.id" :truck-number="truck.number" />
        at the {{ stopType }}:
      </div>
      <div class="rounded bg-grey-lighten-4 pa-2 mb-8">
        <router-link
          :to="{ name: 'facilities-edit', params: { id: stop.facility?.id } }"
          class="text-primary font-weight-500 text-decoration-none d-block"
          data-qa="card-facility-name"
          target="_blank"
        >
          {{ stop.facility?.name }}
        </router-link>
        <div class="mt-1 font-size-12 text-grey-darken-2" data-qa="card-facility-address">
          {{ stop.facility?.addressLine }}, {{ stop.facility?.address }}
        </div>
      </div>
      <v-row>
        <v-col cols="4">
          <timezone-select
            v-model="goodToGoData.timezone"
            :items="timezonesShortList"
            :rules="[requiredValidator]"
            required
          />
        </v-col>
        <v-col cols="5">
          <date-picker v-model="goodToGoData.date" label="Date" required></date-picker>
        </v-col>
        <v-col cols="3">
          <time-picker v-model="goodToGoData.time" label="Time" required></time-picker>
        </v-col>
      </v-row>
    </v-form>
  </omni-dialog>
</template>

<style lang="scss" scoped></style>
